import type { CustomerRowView, Option } from '@today/ui-domain'
import type { CustomerDTO, OrgUnitOption } from '@/services/customer.service'

/**
 * Pure helpers shared by the customer hooks (TO-418 customer-ui): DTO → view
 * mapping, error-code extraction and the machine-code → field map.
 */

/** Stable machine code of an API error (`error.message` prefix before `: `), or null. */
export const errorCodeOf = (error: unknown): string | null => {
	const message = (error as { response?: { data?: { error?: { message?: unknown } } } })?.response?.data?.error?.message
	if (typeof message !== 'string') return null
	const [code] = message.split(': ')
	return /^[A-Z_]+$/.test(code) ? code : null
}

/** HTTP status of an API error, or null. */
export const statusOf = (error: unknown): number | null =>
	(error as { response?: { status?: number } })?.response?.status ?? null

/** Which form field a server machine code belongs to (form-level when absent). */
const FIELD_OF_CODE: Record<string, string> = {
	CUSTOMER_ORG_UNIT_INVALID: 'org_unit_id',
	CUSTOMER_ORG_UNIT_OUT_OF_SCOPE: 'org_unit_id',
	CUSTOMER_ASSIGNED_USER_INVALID: 'assigned_user_id',
	CUSTOMER_PREFERRED_CHANNEL_UNREACHABLE: 'preferred_channel',
	CUSTOMER_CONTACT_POINT_DUPLICATE: 'contact_0',
	CUSTOMER_CONTACT_POINT_INVALID: 'contact_0',
	CUSTOMER_TYPE_DOWNGRADE_FORBIDDEN: 'type',
}

export type Translate = (key: string, vars?: Record<string, string | number>) => string

/** Translated message for a server error; unknown codes fall back to the generic message. */
export const errorMessage = (error: unknown, t: Translate, exists: (key: string) => boolean): string => {
	const code = errorCodeOf(error)
	return code && exists(`errors.${code}`) ? t(`errors.${code}`) : t('errors.generic')
}

/** `{ field, message }` for a server error on the form. */
export const fieldError = (error: unknown, t: Translate, exists: (key: string) => boolean) => {
	const code = errorCodeOf(error)
	return { field: (code && FIELD_OF_CODE[code]) || 'form', message: errorMessage(error, t, exists) }
}

export const formatDate = (iso: string | null, locale: string): string =>
	iso ? new Intl.DateTimeFormat(locale, { dateStyle: 'short' }).format(new Date(iso)) : ''

export const toRow = (c: CustomerDTO, locale: string): CustomerRowView => ({
	id: c.customerId,
	fullName: c.fullName,
	type: c.type,
	status: c.status,
	primaryEmail: c.primaryEmail?.value ?? null,
	primaryMobile: c.primaryMobile?.value ?? null,
	tags: c.tags,
	orgUnitName: c.orgUnit.name,
	assignedUserName: c.assignedUser.displayName,
	createdAt: formatDate(c.createdAt, locale),
})

/** Units as picker options, indented by depth so the hierarchy reads top-down. */
export const unitOptions = (units: OrgUnitOption[]): Option[] => {
	const children = new Map<string | null, OrgUnitOption[]>()
	for (const unit of units) {
		const parent = units.some((u) => u.id === unit.parentId) ? unit.parentId : null
		children.set(parent, [...(children.get(parent) ?? []), unit])
	}
	const out: Option[] = []
	const walk = (parent: string | null, depth: number) => {
		for (const unit of (children.get(parent) ?? []).sort((a, b) => a.code.localeCompare(b.code))) {
			out.push({ id: unit.id, label: `${'  '.repeat(depth)}${unit.name}` })
			walk(unit.id, depth + 1)
		}
	}
	walk(null, 0)
	return out
}
